/**
 * Signup Success Page
 * 
 * Handles the redirect from Stripe Checkout after successful payment.
 * 
 * Flow:
 * 1. User completes Stripe Checkout
 * 2. Stripe redirects to /signup/success?session_id={CHECKOUT_SESSION_ID}
 * 3. This page calls backend to verify session and create account
 * 4. Backend creates User + Org, returns full user/org objects
 * 5. We set auth state directly, then redirect to Onboarding
 */
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { useSafeMode } from '../contexts/SafeModeContext';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config/api';

const SignupSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { safeMode } = useSafeMode();
  const { setUser, setOrganization, setLoading } = useAuth();
  const hasVerified = useRef(false);
  
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [error, setError] = useState(null);
  
  const sessionId = searchParams.get('session_id');
  
  useEffect(() => {
    // Prevent double execution in StrictMode
    if (hasVerified.current) return;
    hasVerified.current = true;
    
    if (!sessionId) {
      setStatus('error');
      setError('No session ID provided. Please try signing up again.');
      return;
    }
    
    const verifyAndCreateAccount = async () => {
      const traceId = `signup_${Date.now()}`;
      console.log(`[SignupSuccess][${traceId}] Starting verify-checkout for session: ${sessionId}`);
      
      try {
        console.log(`[SignupSuccess][${traceId}] Calling /api/auth/verify-checkout...`);
        const response = await fetch(`${API_BASE_URL}/api/auth/verify-checkout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ session_id: sessionId }),
        });
        
        console.log(`[SignupSuccess][${traceId}] Response status: ${response.status}`);
        console.log(`[SignupSuccess][${traceId}] Response headers:`, Object.fromEntries(response.headers.entries()));
        
        if (!response.ok) {
          const data = await response.json();
          console.error(`[SignupSuccess][${traceId}] verify-checkout FAILED:`, data);
          throw new Error(data.detail || 'Failed to verify payment');
        }
        
        const data = await response.json();
        console.log(`[SignupSuccess][${traceId}] verify-checkout SUCCESS:`, {
          success: data.success,
          hasUser: !!data.user,
          hasOrg: !!data.organization,
          userId: data.user?.user_id,
          teamRole: data.user?.teamRole
        });
        
        // SET AUTH STATE DIRECTLY - no need to refetch
        if (data.user && data.organization) {
          console.log(`[SignupSuccess][${traceId}] Setting auth state directly from verify-checkout response`);
          setUser(data.user);
          setOrganization(data.organization);
          setLoading(false);
        }
        
        // Account created successfully
        setStatus('success');
        
        // Short delay to show success message, then redirect to onboarding
        console.log(`[SignupSuccess][${traceId}] Setting 1500ms timeout before navigation to /onboarding`);
        setTimeout(() => {
          console.log(`[SignupSuccess][${traceId}] Timeout fired - navigating to /onboarding NOW`);
          navigate('/onboarding', { replace: true });
        }, 1500);
        
      } catch (err) {
        console.error(`[SignupSuccess][${traceId}] EXCEPTION:`, err);
        setStatus('error');
        setError(err.message || 'Something went wrong. Please contact support.');
      }
    };
    
    verifyAndCreateAccount();
  }, [sessionId, navigate, setUser, setOrganization, setLoading]);
  
  // Processing state
  if (status === 'processing') {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        safeMode ? 'bg-gray-50' : 'bg-slate-950'
      }`}>
        <Card className={`p-8 max-w-md text-center ${
          safeMode ? 'bg-white' : 'bg-slate-900 border-gray-800'
        }`}>
          <Loader2 className={`w-12 h-12 animate-spin mx-auto mb-4 ${
            safeMode ? 'text-blue-600' : 'text-red-500'
          }`} />
          <h1 className={`text-xl font-semibold mb-2 ${
            safeMode ? 'text-gray-900' : 'text-white'
          }`}>
            Setting up your account...
          </h1>
          <p className={safeMode ? 'text-gray-600' : 'text-gray-400'}>
            Please wait while we complete your registration.
          </p>
        </Card>
      </div>
    );
  }
  
  // Success state
  if (status === 'success') {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        safeMode ? 'bg-gray-50' : 'bg-slate-950'
      }`}>
        <Card className={`p-8 max-w-md text-center ${
          safeMode ? 'bg-white' : 'bg-slate-900 border-gray-800'
        }`}>
          <CheckCircle className={`w-12 h-12 mx-auto mb-4 ${
            safeMode ? 'text-green-600' : 'text-green-500'
          }`} />
          <h1 className={`text-xl font-semibold mb-2 ${
            safeMode ? 'text-gray-900' : 'text-white'
          }`}>
            Payment Successful!
          </h1>
          <p className={safeMode ? 'text-gray-600' : 'text-gray-400'}>
            Your account has been created. Redirecting to setup...
          </p>
        </Card>
      </div>
    );
  }
  
  // Error state
  return (
    <div className={`min-h-screen flex items-center justify-center ${
      safeMode ? 'bg-gray-50' : 'bg-slate-950'
    }`}>
      <Card className={`p-8 max-w-md text-center ${
        safeMode ? 'bg-white' : 'bg-slate-900 border-gray-800'
      }`}>
        <AlertCircle className={`w-12 h-12 mx-auto mb-4 ${
          safeMode ? 'text-red-600' : 'text-red-500'
        }`} />
        <h1 className={`text-xl font-semibold mb-2 ${
          safeMode ? 'text-gray-900' : 'text-white'
        }`}>
          Something went wrong
        </h1>
        <p className={`mb-6 ${safeMode ? 'text-gray-600' : 'text-gray-400'}`}>
          {error}
        </p>
        <div className="space-y-3">
          <Button
            onClick={() => navigate('/pricing')}
            className={`w-full ${
              safeMode 
                ? 'bg-blue-600 hover:bg-blue-700' 
                : 'bg-red-600 hover:bg-red-700'
            }`}
          >
            Try Again
          </Button>
          <Button
            variant="outline"
            onClick={() => window.location.href = 'mailto:support@stackranked.ai'}
            className="w-full"
          >
            Contact Support
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default SignupSuccessPage;
